/**
 * Hook para gerenciamento de Event Types do módulo de Agendamento Online
 * 
 * RELEASE: 2026-02-14
 * VERSION: 1.0.0
 * 
 * Funcionalidades:
 * - Listagem de event types do tenant atual
 * - Criação via RPC create_scheduling_event_type
 * - Atualização de event types
 * - Toggle ativo/inativo
 * - Exclusão de event types
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('SchedulingEventTypes');

// ============================================================================
// TIPOS
// ============================================================================

/** Tipo completo de um Event Type retornado pelo banco */
export interface SchedulingEventType {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  duration_minutes: number;
  color: string | null;
  is_active: boolean;
  timezone: string;
  min_notice_hours: number;
  max_advance_days: number;
  max_bookings_per_day: number | null;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  conferencing_provider: string;
  requires_approval: boolean;
  confirmation_message: string | null;
  custom_questions: any | null;
  logo_url: string | null;
  user_id: string;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  /** Campos de integração CRM (v1.3.0) */
  crm_auto_create_deal: boolean;
  crm_workspace_id: string | null;
  crm_pipeline_id: string | null;
  crm_phase_id: string | null;
  crm_source: string | null;
  crm_seller_assignment_mode: string;
  crm_seller_id: string | null;
  crm_distribution_queue_id: string | null;
  /** Quantidade máxima de agendamentos concorrentes no mesmo slot (v1.5.0) */
  max_concurrent_bookings_per_slot: number;
  /** FK para calendar_integrations - calendário vinculado a este tipo de evento (v2.0.0) */
  calendar_integration_id: string | null;
}

/** Dados para criação de um novo Event Type */
export interface CreateEventTypeInput {
  title: string;
  slug: string;
  description?: string;
  duration_minutes?: number;
  color?: string;
  timezone?: string;
  min_notice_hours?: number;
  max_advance_days?: number;
  buffer_before_minutes?: number;
  buffer_after_minutes?: number;
  conferencing_provider?: string;
}

/** Dados para atualização de um Event Type existente */
export interface UpdateEventTypeInput {
  id: string;
  title?: string;
  description?: string | null;
  duration_minutes?: number;
  color?: string | null;
  is_active?: boolean;
  timezone?: string;
  min_notice_hours?: number;
  max_advance_days?: number;
  max_bookings_per_day?: number | null;
  buffer_before_minutes?: number;
  buffer_after_minutes?: number;
  conferencing_provider?: string;
  requires_approval?: boolean;
  confirmation_message?: string | null;
  custom_questions?: any | null;
  logo_url?: string | null;
  /** Campos de integração CRM (v1.3.0) */
  crm_auto_create_deal?: boolean;
  crm_workspace_id?: string | null;
  crm_pipeline_id?: string | null;
  crm_phase_id?: string | null;
  crm_source?: string | null;
  crm_seller_assignment_mode?: string;
  crm_seller_id?: string | null;
  crm_distribution_queue_id?: string | null;
  /** Quantidade máxima de agendamentos concorrentes no mesmo slot (v1.5.0) */
  max_concurrent_bookings_per_slot?: number;
  /** FK para calendar_integrations - calendário vinculado a este tipo de evento (v2.0.0) */
  calendar_integration_id?: string | null;
}

/** Disponibilidade semanal de um Event Type */
export interface SchedulingAvailability {
  id: string;
  event_type_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_available: boolean;
  slot_interval_minutes: number;
}

/** Exceção/bloqueio de data para um Event Type */
export interface SchedulingDateOverride {
  id: string;
  event_type_id: string;
  override_date: string | null;
  is_blocked: boolean;
  start_time: string | null;
  end_time: string | null;
  created_at: string;
  /** Tipo da exceção: 'date' (pontual) ou 'recurring' (recorrente semanal) */
  override_type: 'date' | 'recurring';
  /** Dia da semana (0=dom, 1=seg... 6=sab) - usado quando override_type='recurring' */
  day_of_week: number | null;
  /** Descrição opcional (ex: "Horário de almoço") */
  description: string | null;
}

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export function useSchedulingEventTypes() {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // ---- Listagem de Event Types ----
  const {
    data: eventTypes = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['scheduling-event-types', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      logger.info('Buscando event types do tenant via RPC');

      const { data, error } = await supabase.rpc('get_scheduling_event_types', {
        p_active_only: false,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao buscar event types', error);
        throw error;
      }

      return (data || []) as SchedulingEventType[];
    },
    enabled: !!tenantId,
  });

  // ---- Criação de Event Type via RPC ----
  const createMutation = useMutation({
    mutationFn: async (input: CreateEventTypeInput) => {
      logger.info('Criando event type via RPC', { title: input.title });

      const { data, error } = await supabase.rpc('create_scheduling_event_type', {
        p_title: input.title,
        p_slug: input.slug,
        p_description: input.description || null,
        p_duration_minutes: input.duration_minutes || 30,
        p_color: input.color || '#3B82F6',
        p_timezone: input.timezone || 'America/Sao_Paulo',
        p_min_notice_hours: input.min_notice_hours || 1,
        p_max_advance_days: input.max_advance_days || 60,
        p_buffer_before: input.buffer_before_minutes || 0,
        p_buffer_after: input.buffer_after_minutes || 0,
        p_conferencing_provider: input.conferencing_provider || 'google_meet',
      });

      if (error) {
        logger.error('Erro ao criar event type', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling-event-types', tenantId] });
      toast.success('Tipo de evento criado com sucesso!');
    },
    onError: (error: any) => {
      logger.error('Falha ao criar event type', error);
      toast.error(error.message || 'Erro ao criar tipo de evento');
    },
  });

  // ---- Atualização de Event Type ----
  const updateMutation = useMutation({
    mutationFn: async (input: UpdateEventTypeInput) => {
      const { id, ...updates } = input;
      logger.info('Atualizando event type via RPC', { id });

      const { error } = await supabase.rpc('update_scheduling_event_type', {
        p_event_type_id: id,
        p_updates: updates as any,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao atualizar event type', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling-event-types', tenantId] });
      toast.success('Tipo de evento atualizado!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erro ao atualizar');
    },
  });

  // ---- Toggle ativo/inativo ----
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      logger.info('Toggle event type via RPC', { id, is_active });

      const { error } = await supabase.rpc('update_scheduling_event_type', {
        p_event_type_id: id,
        p_updates: { is_active } as any,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling-event-types', tenantId] });
    },
    onError: (error: any) => {
      toast.error('Erro ao alterar status');
    },
  });

  // ---- Exclusão de Event Type via RPC atômica ----
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      logger.info('Excluindo event type via RPC', { id });

      const { error } = await supabase.rpc('delete_scheduling_event_type', {
        p_event_type_id: id,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling-event-types', tenantId] });
      toast.success('Tipo de evento excluído!');
    },
    onError: (error: any) => {
      toast.error('Erro ao excluir tipo de evento');
    },
  });

  return {
    eventTypes,
    isLoading,
    error,
    createEventType: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateEventType: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    toggleActive: toggleActiveMutation.mutate,
    deleteEventType: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}

// ============================================================================
// HOOK PARA DISPONIBILIDADE DE UM EVENT TYPE
// ============================================================================

export function useSchedulingAvailability(eventTypeId: string | null) {
  const queryClient = useQueryClient();

  const {
    data: availability = [],
    isLoading,
  } = useQuery({
    queryKey: ['scheduling-availability', eventTypeId],
    queryFn: async () => {
      if (!eventTypeId) return [];

      const { data, error } = await supabase
        .from('scheduling_availability')
        .select('*')
        .eq('event_type_id', eventTypeId)
        .order('day_of_week', { ascending: true });

      if (error) throw error;
      return data as SchedulingAvailability[];
    },
    enabled: !!eventTypeId,
  });

  // Atualização em lote da disponibilidade
  const updateAvailabilityMutation = useMutation({
    mutationFn: async (items: Partial<SchedulingAvailability>[]) => {
      logger.info('Atualizando disponibilidade', { eventTypeId, count: items.length });

      // Atualiza cada item individualmente
      for (const item of items) {
        if (!item.id) continue;
        const { id, ...updates } = item;
        const { error } = await supabase
          .from('scheduling_availability')
          .update(updates)
          .eq('id', id);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling-availability', eventTypeId] });
      toast.success('Disponibilidade atualizada!');
    },
    onError: () => {
      toast.error('Erro ao atualizar disponibilidade');
    },
  });

  return {
    availability,
    isLoading,
    updateAvailability: updateAvailabilityMutation.mutateAsync,
    isUpdating: updateAvailabilityMutation.isPending,
  };
}

// ============================================================================
// HOOK PARA DATE OVERRIDES DE UM EVENT TYPE
// ============================================================================

export function useSchedulingDateOverrides(eventTypeId: string | null) {
  const queryClient = useQueryClient();

  const {
    data: overrides = [],
    isLoading,
  } = useQuery({
    queryKey: ['scheduling-date-overrides', eventTypeId],
    queryFn: async () => {
      if (!eventTypeId) return [];

      // (v1.7.0) Filtrar apenas exceções pontuais (por data)
      const { data, error } = await supabase
        .from('scheduling_date_overrides')
        .select('*')
        .eq('event_type_id', eventTypeId)
        .eq('override_type', 'date')
        .order('override_date', { ascending: true });

      if (error) throw error;
      return data as SchedulingDateOverride[];
    },
    enabled: !!eventTypeId,
  });

  // Criação de date override
  const createOverrideMutation = useMutation({
    mutationFn: async (input: {
      override_date: string;
      is_blocked: boolean;
      start_time?: string;
      end_time?: string;
    }) => {
      if (!eventTypeId) throw new Error('Event type não selecionado');

      const { error } = await supabase
        .from('scheduling_date_overrides')
        .insert({
          event_type_id: eventTypeId,
          override_date: input.override_date,
          is_blocked: input.is_blocked,
          start_time: input.start_time || null,
          end_time: input.end_time || null,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling-date-overrides', eventTypeId] });
      toast.success('Exceção de data adicionada!');
    },
    onError: () => {
      toast.error('Erro ao adicionar exceção');
    },
  });

  // Exclusão de date override
  const deleteOverrideMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('scheduling_date_overrides')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling-date-overrides', eventTypeId] });
      toast.success('Exceção removida!');
    },
    onError: () => {
      toast.error('Erro ao remover exceção');
    },
  });

  return {
    overrides,
    isLoading,
    createOverride: createOverrideMutation.mutateAsync,
    deleteOverride: deleteOverrideMutation.mutateAsync,
    isCreating: createOverrideMutation.isPending,
  };
}

// ============================================================================
// HOOK PARA EXCEÇÕES RECORRENTES (POR DIA DA SEMANA)
// @release v1.7.0 - 2026-04-10
// ============================================================================

export function useSchedulingRecurringOverrides(eventTypeId: string | null) {
  const queryClient = useQueryClient();

  /** Lista exceções recorrentes do event type */
  const {
    data: recurringOverrides = [],
    isLoading,
  } = useQuery({
    queryKey: ['scheduling-recurring-overrides', eventTypeId],
    queryFn: async () => {
      if (!eventTypeId) return [];

      const { data, error } = await supabase
        .from('scheduling_date_overrides')
        .select('*')
        .eq('event_type_id', eventTypeId)
        .eq('override_type', 'recurring')
        .order('day_of_week', { ascending: true });

      if (error) throw error;
      return data as SchedulingDateOverride[];
    },
    enabled: !!eventTypeId,
  });

  /** Criação de exceções recorrentes (aceita array de dias) */
  const createRecurringMutation = useMutation({
    mutationFn: async (input: {
      days_of_week: number[];
      is_blocked: boolean;
      start_time: string;
      end_time: string;
      description?: string;
    }) => {
      if (!eventTypeId) throw new Error('Event type não selecionado');

      // Inserir uma linha para cada dia selecionado
      const rows = input.days_of_week.map((dow) => ({
        event_type_id: eventTypeId,
        override_type: 'recurring' as const,
        day_of_week: dow,
        is_blocked: input.is_blocked,
        start_time: input.start_time,
        end_time: input.end_time,
        description: input.description || null,
        override_date: null,
      }));

      const { error } = await supabase
        .from('scheduling_date_overrides')
        .insert(rows);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling-recurring-overrides', eventTypeId] });
      toast.success('Exceção recorrente adicionada!');
    },
    onError: (err: any) => {
      // Tratar erro de duplicata
      if (err?.code === '23505') {
        toast.error('Já existe uma exceção recorrente para este dia/horário');
      } else {
        toast.error('Erro ao adicionar exceção recorrente');
      }
    },
  });

  /** Exclusão de exceção recorrente */
  const deleteRecurringMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('scheduling_date_overrides')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling-recurring-overrides', eventTypeId] });
      toast.success('Exceção recorrente removida!');
    },
    onError: () => {
      toast.error('Erro ao remover exceção recorrente');
    },
  });

  return {
    recurringOverrides,
    isLoading,
    createRecurringOverride: createRecurringMutation.mutateAsync,
    deleteRecurringOverride: deleteRecurringMutation.mutateAsync,
    isCreating: createRecurringMutation.isPending,
  };
}

// ============================================================================
// HOOK PARA INTEGRAÇÃO COM GOOGLE CALENDAR
// ============================================================================

export function useCalendarIntegration() {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  const {
    data: integration,
    isLoading,
  } = useQuery({
    queryKey: ['calendar-integration', tenantId, user?.id],
    queryFn: async () => {
      if (!tenantId || !user?.id) return null;

      // Busca TODAS as integrações do usuário (ativas e inativas)
      const { data, error } = await supabase
        .from('calendar_integrations')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('user_id', user.id)
        .order('is_active', { ascending: false })
        .order('updated_at', { ascending: false });

      if (error) {
        logger.error('Erro ao buscar integrações de calendário do usuário', error);
        return [];
      }

      return data || [];
    },
    enabled: !!tenantId && !!user?.id,
  });

  // Integração ativa (primeira da lista, já ordenada)
  const allIntegrations = integration || [];
  const activeIntegration = allIntegrations.find((i: any) => i.is_active) || null;
  const inactiveIntegrations = allIntegrations.filter((i: any) => !i.is_active);

  // Desconectar integração (is_active = false)
  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!activeIntegration?.id) throw new Error('Sem integração ativa');

      const { error } = await supabase
        .from('calendar_integrations')
        .update({ is_active: false })
        .eq('id', activeIntegration.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-integration'] });
      toast.success('Google Calendar desconectado!');
    },
    onError: () => {
      toast.error('Erro ao desconectar');
    },
  });

  // Excluir integração permanentemente (DELETE)
  const deleteCalendarMutation = useMutation({
    mutationFn: async (integrationId: string) => {
      logger.info('Excluindo integração de calendário permanentemente', { integrationId });

      const { error } = await supabase
        .from('calendar_integrations')
        .delete()
        .eq('id', integrationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-integration'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-integrations'] });
      toast.success('Calendário excluído permanentemente!');
    },
    onError: () => {
      toast.error('Erro ao excluir calendário');
    },
  });

  // Atualiza preferências da integração (corporativa / sync ativo)
  const updateSettingsMutation = useMutation({
    mutationFn: async (
      payload: { id: string; is_corporate?: boolean; sync_enabled?: boolean }
    ) => {
      const patch: { is_corporate?: boolean; sync_enabled?: boolean } = {};
      if (payload.is_corporate !== undefined) patch.is_corporate = payload.is_corporate;
      if (payload.sync_enabled !== undefined) patch.sync_enabled = payload.sync_enabled;

      const { error } = await supabase
        .from('calendar_integrations')
        .update(patch)
        .eq('id', payload.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-integration'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-integrations'] });
      toast.success('Preferências atualizadas');
    },
    onError: (e: any) => {
      logger.error('Erro ao atualizar preferências da integração', e);
      toast.error('Erro ao salvar preferência');
    },
  });

  return {
    integration: activeIntegration,
    inactiveIntegrations,
    isConnected: !!activeIntegration,
    isLoading,
    disconnect: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
    deleteIntegration: deleteCalendarMutation.mutateAsync,
    isDeleting: deleteCalendarMutation.isPending,
    updateSettings: updateSettingsMutation.mutateAsync,
    isUpdatingSettings: updateSettingsMutation.isPending,
  };
}

// ============================================================================
// HOOK PARA LISTAR TODAS AS INTEGRAÇÕES DE CALENDÁRIO DO TENANT
// ============================================================================

/** Tipo de integração de calendário */
export interface CalendarIntegrationItem {
  id: string;
  provider: string;
  provider_email: string | null;
  calendar_id: string | null;
  is_active: boolean;
  user_id: string;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  // Campos do Calendário Global (Fase 1)
  is_corporate: boolean;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_sync_error: string | null;
  watch_expires_at: string | null;
}

/**
 * Lista TODAS as integrações de calendário do tenant atual.
 * Usado no modal de calendários e no select de vínculo por tipo de evento.
 */
export function useCalendarIntegrations() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const {
    data: integrations = [],
    isLoading,
  } = useQuery({
    queryKey: ['calendar-integrations', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      logger.info('Buscando todas as integrações de calendário do tenant');

      // Inclui campos do Calendário Global (is_corporate, sync_enabled, last_sync_*)
      const { data, error } = await supabase
        .from('calendar_integrations')
        .select('id, provider, provider_email, calendar_id, is_active, user_id, tenant_id, created_at, updated_at, is_corporate, sync_enabled, last_sync_at, last_sync_error, watch_expires_at')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Erro ao buscar integrações de calendário', error);
        throw error;
      }

      return data as CalendarIntegrationItem[];
    },
    enabled: !!tenantId,
  });

  /** Filtra apenas integrações ativas (útil para selects) */
  const activeIntegrations = integrations.filter((i) => i.is_active);

  return {
    integrations,
    activeIntegrations,
    isLoading,
  };
}
